import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import filterParams from '../utils/filterParams';
import { brandStatus, STATUS_COLOUR, STATUS_TEXT } from '../utils/evidenceStatus';

// Brand x crop x province x pest/disease explorer. The backend returns one record per
// conversation; every cut is computed here from conversation ids, so a conversation that
// names two crops counts once per crop row but never twice in the same row.

const DIMENSIONS = [
  { id: 'brand', label: 'Brand' },
  { id: 'crop', label: 'Crop' },
  { id: 'province', label: 'Province' },
  { id: 'issue', label: 'Pest / disease' },
];

const PRESETS = [
  { label: 'Brand by province', dims: ['brand', 'province'] },
  { label: 'Brand by crop (where it is used)', dims: ['brand', 'crop'] },
  { label: 'Crop by province', dims: ['crop', 'province'] },
  // Issue presets name a theme from the org config; shown only when the org defines it.
  { label: 'Pests by province', dims: ['issue', 'province'], theme: 'Pests' },
  { label: 'Diseases by province', dims: ['issue', 'province'], theme: 'Diseases' },
  { label: 'Brand, crop and province', dims: ['brand', 'crop', 'province'] },
];

const MAX_ROWS = 200;
const MIN_CONVERSATIONS = 3; // below this a row is shown as not enough evidence

// The values a conversation contributes to one dimension, after that dimension's filter.
function valuesFor(record, dim, filter) {
  let values;
  if (dim === 'brand') values = record.brands.map(([name]) => name);
  else if (dim === 'crop') values = record.crops;
  else if (dim === 'province') values = [record.province];
  else
    values = record.issues
      .filter(([, theme]) => !filter.theme || theme === filter.theme)
      .map(([name]) => name);
  values = [...new Set(values)];
  return filter[dim] ? values.filter((v) => v === filter[dim]) : values;
}

function cross(lists) {
  return lists.reduce((acc, list) => acc.flatMap((prefix) => list.map((v) => [...prefix, v])), [[]]);
}

export function computeCuts(records, dims, filter) {
  const constrained = DIMENSIONS.map((d) => d.id).filter(
    (id) => !dims.includes(id) && (filter[id] || (id === 'issue' && filter.theme)),
  );
  const rows = new Map();
  records.forEach((record) => {
    // A filtered dimension that is not a breakdown still has to be present.
    if (constrained.some((id) => !valuesFor(record, id, filter).length)) return;
    const combos = cross(dims.map((id) => valuesFor(record, id, filter)));
    combos.forEach((combo) => {
      const key = combo.join('\u0000');
      if (!rows.has(key)) rows.set(key, { values: combo, ids: new Set(), sentiment: {} });
      const row = rows.get(key);
      row.ids.add(record.id);
      // Sentiment belongs to one brand: the row's brand, or the filtered brand.
      const brand = dims.includes('brand') ? combo[dims.indexOf('brand')] : filter.brand;
      if (brand) {
        const scored = record.brands.find(([name]) => name === brand);
        if (scored) row.sentiment[record.id] = scored[1];
      }
    });
  });
  return [...rows.values()]
    .map((row) => {
      const labels = Object.values(row.sentiment);
      const count = (s) => labels.filter((x) => x === s).length;
      const scored = labels.length;
      const net = scored ? Math.round(((count('positive') - count('negative')) * 100) / scored) : null;
      return { values: row.values, conversations: row.ids.size, scored, net };
    })
    .sort((a, b) => b.conversations - a.conversations);
}

export default function CutsExplorer({ filters }) {
  const [data, setData] = useState({ conversations: [], issue_themes: [] });
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [dims, setDims] = useState(['brand', 'province']);
  const [filter, setFilter] = useState({ brand: '', crop: '', province: '', issue: '', theme: '' });

  useEffect(() => {
    const controller = new AbortController();
    setBusy(true);
    setError('');
    axios
      .get(`/api/decision/cuts?${filterParams(filters)}`, { signal: controller.signal })
      .then((r) => setData(r.data || { conversations: [], issue_themes: [] }))
      .catch((e) => {
        if (!axios.isCancel(e)) setError('The brand, crop and region breakdown could not be loaded.');
      })
      .finally(() => setBusy(false));
    return () => controller.abort();
  }, [filters]);

  const options = useMemo(() => {
    const sets = { brand: new Set(), crop: new Set(), province: new Set(), issue: new Set() };
    data.conversations.forEach((r) => {
      r.brands.forEach(([name]) => sets.brand.add(name));
      r.crops.forEach((name) => sets.crop.add(name));
      sets.province.add(r.province);
      r.issues.forEach(([name, theme]) => (!filter.theme || theme === filter.theme) && sets.issue.add(name));
    });
    return Object.fromEntries(Object.entries(sets).map(([k, v]) => [k, [...v].sort()]));
  }, [data, filter.theme]);

  const rows = useMemo(() => computeCuts(data.conversations, dims, filter), [data, dims, filter]);
  const showSentiment = dims.includes('brand') || Boolean(filter.brand);

  const toggle = (id) =>
    setDims((current) =>
      current.includes(id)
        ? current.filter((x) => x !== id)
        : DIMENSIONS.map((d) => d.id).filter((x) => x === id || current.includes(x)),
    );
  const setOne = (key, value) => setFilter((f) => ({ ...f, [key]: value, ...(key === 'theme' ? { issue: '' } : {}) }));

  if (busy) return <p className="analytics-empty">Loading the brand, crop and region breakdown…</p>;
  if (error) return <p className="analytics-warning">{error}</p>;

  return (
    <div className="cuts-explorer">
      <div className="analytics-subtabs cuts-presets">
        {PRESETS.filter((p) => !p.theme || data.issue_themes.includes(p.theme)).map((p) => (
          <button
            key={p.label}
            type="button"
            className={p.dims.join() === dims.join() && (p.theme || '') === filter.theme ? 'active' : ''}
            onClick={() => {
              setDims(p.dims);
              setFilter((f) => ({ ...f, theme: p.theme || '', issue: '' }));
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="analytics-selectors">
        <fieldset className="cuts-dims">
          <legend>Break down by</legend>
          {DIMENSIONS.map((d) => (
            <label key={d.id}>
              <input type="checkbox" checked={dims.includes(d.id)} onChange={() => toggle(d.id)} /> {d.label}
            </label>
          ))}
        </fieldset>
        {DIMENSIONS.map((d) => (
          <label key={d.id}>
            {d.label}
            <select value={filter[d.id]} onChange={(e) => setOne(d.id, e.target.value)}>
              <option value="">All</option>
              {options[d.id].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </label>
        ))}
        <label>
          Issue type
          <select value={filter.theme} onChange={(e) => setOne('theme', e.target.value)}>
            <option value="">All</option>
            {data.issue_themes.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
      </div>
      {!dims.length ? (
        <p className="analytics-empty">Choose at least one dimension to break down by.</p>
      ) : (
        <div className="analytics-table-wrap">
          <table className="analytics-table">
            <thead>
              <tr>
                {dims.map((id) => (
                  <th key={id}>{DIMENSIONS.find((d) => d.id === id).label}</th>
                ))}
                <th>Conversations</th>
                {showSentiment && <th>Scored for sentiment</th>}
                {showSentiment && <th>Net sentiment</th>}
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, MAX_ROWS).map((row) => {
                const status = showSentiment
                  ? brandStatus({ total: row.scored, net_sentiment: row.net })
                  : row.conversations < MIN_CONVERSATIONS
                    ? 'grey'
                    : null;
                return (
                  <tr key={row.values.join('|')}>
                    {row.values.map((v, i) => (
                      <td key={dims[i]}>{v}</td>
                    ))}
                    <td>{row.conversations}</td>
                    {showSentiment && <td>{row.scored}</td>}
                    {showSentiment && <td>{row.net == null ? '—' : `${row.net > 0 ? '+' : ''}${row.net}`}</td>}
                    <td>
                      {status && (
                        <span className="cuts-status" style={{ background: STATUS_COLOUR[status] }}>
                          {STATUS_TEXT[status]}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <p className="analytics-empty">No conversations match this combination.</p>}
          {rows.length > MAX_ROWS && (
            <p className="analytics-method">
              Showing the {MAX_ROWS} largest of {rows.length} combinations. Add a filter to narrow the list.
            </p>
          )}
        </div>
      )}
      <p className="analytics-method">
        {data.method_note} Sentiment is shown for the brand in each row (or the selected brand) and follows the
        Brand Intelligence status rule. Label claims are not loaded yet, so a brand-by-crop row shows where farmers
        mention using a brand, not whether that use is on the label.
      </p>
    </div>
  );
}
